<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

const { t } = useI18n();

const MEDALS = ['🥇', '🥈', '🥉'];

const labelSummary = useMapGetter('summaryReports/getLabelSummaryReports');
const labels = useMapGetter('labels/getLabels');

const topThree = computed(() => {
  const colors = new Map(
    (labels.value || []).map(label => [Number(label.id), label.color])
  );

  return [...(labelSummary.value || [])]
    .filter(report => Number(report.conversationsCount) > 0)
    .sort((left, right) => {
      const byCount =
        Number(right.conversationsCount) - Number(left.conversationsCount);
      if (byCount !== 0) return byCount;
      return (left.name || '').localeCompare(right.name || '');
    })
    .slice(0, 3)
    .map(report => ({
      id: report.id,
      title: report.name,
      color: colors.get(Number(report.id)) || '#1f93ff',
      conversationsCount: Number(report.conversationsCount),
    }));
});

const podium = computed(() => {
  const [first, second, third] = topThree.value;
  return [
    second && { label: second, medal: MEDALS[1], position: 2 },
    first && { label: first, medal: MEDALS[0], position: 1 },
    third && { label: third, medal: MEDALS[2], position: 3 },
  ].filter(Boolean);
});
</script>

<template>
  <div
    v-show="podium.length"
    class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-6"
  >
    <div class="flex flex-col gap-1 text-center">
      <h3 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('LABEL_REPORTS.RANKING.TITLE') }}
      </h3>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('LABEL_REPORTS.RANKING.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex flex-wrap items-end justify-center gap-4 pt-2">
      <div
        v-for="entry in podium"
        :key="entry.label.id"
        class="flex w-44 flex-col items-center gap-2 rounded-xl border bg-n-solid-1 p-4 text-center"
        :class="
          entry.position === 1
            ? 'border-n-brand shadow-md -translate-y-2'
            : 'border-n-weak'
        "
      >
        <span class="text-2xl leading-none">{{ entry.medal }}</span>
        <span
          class="size-8 rounded-full border border-n-weak"
          :style="{ backgroundColor: entry.label.color }"
        />
        <span class="max-w-full truncate text-sm font-medium text-n-slate-12">
          {{ entry.label.title }}
        </span>
        <span
          v-if="entry.position === 1"
          class="text-xs font-medium text-n-amber-11"
        >
          {{ t('LABEL_REPORTS.RANKING.TOP_LABEL') }}
        </span>
        <span class="text-sm font-semibold text-n-blue-text">
          {{
            t(
              'LABEL_REPORTS.RANKING.CONVERSATION_COUNT',
              { count: entry.label.conversationsCount },
              entry.label.conversationsCount
            )
          }}
        </span>
      </div>
    </div>
  </div>
</template>
